// SPDX-License-Identifier: AGPL-3.0-or-later

// Micup akis-v3: a video post's poster taken from a frame of the picked video, in the browser. It is uploaded and checked
// like any post image; the server never decodes the video for it.
export const FEED_POSTER_FILENAME = 'kapak.jpg';
const POSTER_MAX_SIDE = 1920;
const POSTER_JPEG_QUALITY = 0.9;
// A file the browser cannot open (a codec it lacks) never answers; past this the frame choice is not offered.
const PROBE_TIMEOUT_MS = 8000;

export interface FeedVideoProbe {
	// Seconds, null when the browser could not read it.
	duration: number | null;
	// Whether the browser shows its frames, so one can be picked as the poster.
	decodable: boolean;
}

export function posterSize(width: number, height: number): {width: number; height: number} {
	const scale = Math.min(1, POSTER_MAX_SIDE / Math.max(width, height));
	return {width: Math.round(width * scale), height: Math.round(height * scale)};
}

// The length and whether the frames show, from the file's metadata only.
export function probeFeedVideo(url: string): Promise<FeedVideoProbe> {
	return new Promise((resolve) => {
		const video = document.createElement('video');
		const finish = (probe: FeedVideoProbe) => {
			clearTimeout(timer);
			video.onloadedmetadata = null;
			video.onerror = null;
			video.removeAttribute('src');
			video.load();
			resolve(probe);
		};
		const timer = setTimeout(() => finish({duration: null, decodable: false}), PROBE_TIMEOUT_MS);
		video.preload = 'metadata';
		video.muted = true;
		video.onloadedmetadata = () =>
			finish({
				duration: Number.isFinite(video.duration) ? video.duration : null,
				decodable: video.videoWidth > 0 && video.videoHeight > 0,
			});
		video.onerror = () => finish({duration: null, decodable: false});
		video.src = url;
	});
}

// The frame the element shows now, as a JPEG file; null when it shows none or the browser cannot encode it.
export async function posterFromFrame(video: HTMLVideoElement): Promise<File | null> {
	if (video.videoWidth === 0 || video.videoHeight === 0) return null;
	// The slider moved the time just now: the frame shown is the old one until the seek lands.
	if (video.seeking) await new Promise((resolve) => video.addEventListener('seeked', resolve, {once: true}));
	const {width, height} = posterSize(video.videoWidth, video.videoHeight);
	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	const context = canvas.getContext('2d');
	if (!context) return null;
	context.drawImage(video, 0, 0, width, height);
	const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', POSTER_JPEG_QUALITY));
	return blob ? new File([blob], FEED_POSTER_FILENAME, {type: 'image/jpeg'}) : null;
}
